import { Inject, ChangeDetectorRef, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { BehaviorSubject, distinctUntilChanged, filter, map, Subscription, switchMap, take, tap } from 'rxjs';
import { UtilsService, CacheService } from '../services';
import { CONSTANTS_TOKEN } from '../config';

enum ImageStatus {
  Pending = 'pending',
  Success = 'success'
}

@Pipe({
  name: 'useHttpImgSrc',
  pure: false,
  standalone: true
})
export class UseHttpImageSourcePipe implements PipeTransform, OnDestroy {
  private subscription = new Subscription();
  private transformValue = new BehaviorSubject<string>('');
  private latestValue!: string | SafeUrl;

  constructor(
    private httpClient: HttpClient,
    private domSanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private utils: UtilsService,
    private cache: CacheService,
    @Inject(CONSTANTS_TOKEN) private constants: any
  ) {
    this.setUpSubscription();
  }

  transform(
    blobName: string,
    { userId = '', isPhysician = false, isProfile = false, isEvent = false, isSpeaker = false }: any
  ): string | SafeUrl | void {
    if (!!blobName) {
      const blob = this.utils.getBlobPath(blobName);
      let url = this.utils.getEncryptedUrls(
        this.constants.api.baseUrlAuth,
        this.constants.api.privateEndpoints.file,
        `/${blob}?is_physician=${isPhysician}&is_profile=${isProfile}&is_event=${isEvent}&is_speaker=${isSpeaker}`
      );
      if (!!userId) {
        url = this.utils.getEncryptedUrls(
          this.constants.api.baseUrlAuth,
          this.constants.api.privateEndpoints.file,
          `/${userId}/${blob}?is_physician=${isPhysician}&is_profile=${isProfile}&is_event=${isEvent}&is_speaker=${isSpeaker}`
        );
      }
      if (this.cache.imageCache[url]?.status === ImageStatus.Success) {
        return this.cache.imageCache[url]?.url;
      }
      this.transformValue.next(url);
      return this.latestValue;
    }
  }

  private setUpSubscription(): void {
    const transformSubscription = this.transformValue
      .asObservable()
      .pipe(
        filter((v): v is string => !!v),
        distinctUntilChanged(),
        switchMap((imagePath: string) => {
          this.cache.imageCache[imagePath] = { status: ImageStatus.Pending, url: undefined };
          return this.httpClient
            .get(imagePath, {
              observe: 'response',
              responseType: 'blob'
            })
            .pipe(
              take(1),
              map((response: any) => URL.createObjectURL(response.body)),
              map((unsafeBlobUrl: string) => this.domSanitizer.bypassSecurityTrustResourceUrl(unsafeBlobUrl)),
              filter((blobUrl) => {
                this.cache.imageCache[imagePath] = { status: ImageStatus.Success, url: blobUrl };
                return blobUrl !== this.latestValue;
              })
            );
        }),
        tap((imageUrl: string | SafeUrl) => {
          this.latestValue = imageUrl;
          this.cdr.markForCheck();
        })
      )
      .subscribe();
    this.subscription.add(transformSubscription);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
